import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from "react-i18next";
import styles from './style/MyPage.module.css';

// 기존 모달 컴포넌트
import NewPortfolioModal from '../portfolios/modal/NewPortfolioModal';
import RebalanceModal from '../portfolios/modal/RebalanceModal';
import EditNicknameModal from './partials/modal/EditNicknameModal';
import EditIntroductionModal from './partials/modal/EditIntroductionModal';
import FollowListModal from './partials/modal/FollowListModal';

// 새로 분리된 탭 컴포넌트 추가
import ProfileAboutTab from './partials/ProfileAboutTab';
import PortfolioTab from './partials/PortfolioTab';
import PostsTab from './partials/PostsTab';

// ⭐️ 새로운 사용자 정의 훅(Custom Hooks) 임포트
import useConfirmModal from './partials/hooks/useConfirmModal';
import useUserProfile from './partials/hooks/useUserProfile';
import useFollows from './partials//hooks/useFollows';
import usePortfolios from './partials/hooks/usePortfolios';
import useMyPosts from './partials/hooks/useMyPosts';


export default function MyPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const currentUserId = auth.user?.profile?.sub;
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState('about');

  useEffect(() => {
    if (!auth.isAuthenticated) {
      navigate('/login');
    }
  }, [auth.isAuthenticated, navigate]);

  // 1. 공통 확인 모달 로직 추출
  const { 
    showConfirmModal, 
    confirmMessage, 
    confirmAction,
    handleConfirm, 
    executeConfirmAction, 
    cancelConfirmAction 
  } = useConfirmModal(t);

  // 2. 프로필 로직 추출
  const { 
    nickname, 
    introduction, 
    profileImageUrl, 
    loading: loadingProfile, 
    fileInputRef, 
    showEditNicknameModal, 
    setShowEditNicknameModal, 
    showEditIntroductionModal, 
    setShowEditIntroductionModal, 
    handleImageClick, 
    handleProfileImageUpload, 
    handleUpdateNickname, 
    handleUpdateIntroduction 
  } = useUserProfile(auth, t);
  
  // 3. 팔로우 로직 추출
  const { 
    followingList, 
    followerList, 
    loadingFollows, 
    showFollowListModal, 
    followListModalType, 
    handleFollow, 
    handleUnfollow, 
    handleOpenFollowListModal, 
    handleCloseFollowListModal 
  } = useFollows(auth, handleConfirm, t);

  // 4. 포트폴리오 로직 추출
  const { 
    portfolios, 
    loadingPortfolios, 
    averageReturn, 
    averageSharpe, 
    showNewPortfolioModal, 
    setShowNewPortfolioModal, 
    showRebalanceModal, 
    selectedPortfolio, 
    handleOpenRebalanceModal, 
    handleCloseRebalanceModal, 
    handleRebalanced, 
    handleDeletePortfolio 
  } = usePortfolios(auth, handleConfirm, t);

  // 5. 포스트 로직 추출
  const { 
    myPostsTab, 
    setMyPostsTab, 
    feedPosts, 
    loadingFeedPosts, 
    myPosts, 
    loadingPosts, 
    savedPosts, 
    loadingSavedPosts, 
    handleDeletePost, 
    handleUnsavePost 
  } = useMyPosts(auth, handleConfirm, currentUserId, t);

  // 닉네임 상태가 훅으로 이동했지만, 타이틀 변경 로직은 MyPage에서 유지
  useEffect(() => {
    document.title = nickname ? `My Page - ${nickname}` : 'Loading My Page...';
  }, [nickname]);


  return (
    <div className={styles.container}>
      {/* 탭 버튼 */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tabButton} ${activeTab === 'about' ? styles.active : ''}`}
          onClick={() => setActiveTab('about')}
        >
          {t('mypage.profile')}
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'portfolio' ? styles.active : ''}`}
          onClick={() => setActiveTab('portfolio')}
        >
          {t('mypage.portfolio')}
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'posts' ? styles.active : ''}`}
          onClick={() => setActiveTab('posts')}
        >
          {t('mypage.posts')}
        </button>
      </div>

      {/* 1. ProfileAboutTab */}
      {activeTab === 'about' && (
        <ProfileAboutTab
          nickname={nickname}
          introduction={introduction}
          profileImageUrl={profileImageUrl}
          loading={loadingProfile}
          loadingFollows={loadingFollows}
          followingList={followingList}
          followerList={followerList}
          fileInputRef={fileInputRef}
          handleImageClick={handleImageClick}
          handleProfileImageUpload={handleProfileImageUpload}
          setShowEditNicknameModal={setShowEditNicknameModal}
          setShowEditIntroductionModal={setShowEditIntroductionModal}
          handleOpenFollowListModal={handleOpenFollowListModal}
        />
      )}

      {/* 2. PortfolioTab */}
      {activeTab === 'portfolio' && (
        <PortfolioTab
          portfolios={portfolios}
          loadingPortfolios={loadingPortfolios}
          averageReturn={averageReturn}
          averageSharpe={averageSharpe}
          auth={auth}
          setShowNewPortfolioModal={setShowNewPortfolioModal}
          handleOpenRebalanceModal={handleOpenRebalanceModal}
          handleDeletePortfolio={handleDeletePortfolio}
        />
      )}

      {/* 3. PostsTab */}
      {activeTab === 'posts' && (
        <PostsTab
          myPostsTab={myPostsTab}
          setMyPostsTab={setMyPostsTab}
          feedPosts={feedPosts}
          loadingFeedPosts={loadingFeedPosts}
          myPosts={myPosts}
          loadingPosts={loadingPosts}
          savedPosts={savedPosts}
          loadingSavedPosts={loadingSavedPosts}
          handleDeletePost={handleDeletePost}
          handleUnsavePost={handleUnsavePost}
        />
      )}

      {/* --- 모달들 (훅에서 받은 상태/핸들러 사용) --- */}
      {showNewPortfolioModal && auth.isAuthenticated && (
        <NewPortfolioModal
          onClose={() => setShowNewPortfolioModal(false)}
          onCreated={(id) => {
            setShowNewPortfolioModal(false);
            navigate(`/portfolios/${id}`);
            handleRebalanced(); // 포트폴리오 목록 새로고침
          }}
        />
      )}

      {showRebalanceModal && selectedPortfolio && auth.isAuthenticated && (
        <RebalanceModal
          portfolioId={selectedPortfolio.portfolioId}
          onClose={handleCloseRebalanceModal}
          onRebalanced={() => {
            // 1. 기존 새로고침 로직 실행
            handleRebalanced(); 
            
            // 2. 장 마감 후 반영 안내 알림 추가
            alert(t('mypage.rebalanceSuccessMsg') || 
              "리밸런싱이 완료되었습니다. 성과는 선택하신 날짜의 장 마감 종가를 기준으로 계산되어 내일부터 반영됩니다.");
          }}
        />

        // <RebalanceModal
        //   portfolioId={selectedPortfolio.portfolioId}
        //   onClose={handleCloseRebalanceModal}
        //   onRebalanced={handleRebalanced}
          
          
        // />
      )}

      {showEditNicknameModal && auth.isAuthenticated && (
        <EditNicknameModal
          currentNickname={nickname}
          onClose={() => setShowEditNicknameModal(false)}
          onSave={handleUpdateNickname}
        />
      )}

      {showEditIntroductionModal && auth.isAuthenticated && (
        <EditIntroductionModal
          currentIntroduction={introduction}
          onClose={() => setShowEditIntroductionModal(false)}
          onSave={handleUpdateIntroduction}
        />
      )}

      {/* 공통 Confirm Modal */}
      {showConfirmModal && (
          <div className={styles.confirmModalOverlay}>
              <div className={styles.confirmModalContent}> 
                  <p className={styles.confirmMessage}>{confirmMessage}</p> 
                  <div className={styles.confirmActions}> 
                      <button 
                          onClick={executeConfirmAction} 
                          className={styles.confirmBtn}
                      >
                          {t('mypage.confirm0')}
                     
                      </button>
                      <button 
                          onClick={cancelConfirmAction} 
                          className={styles.cancelBtn}
                      >
                          {t('mypage.cancel')}
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* Follow List Modal */}
      {showFollowListModal && auth.isAuthenticated && (
        <FollowListModal
          listType={followListModalType}
          list={followListModalType === 'following' ? followingList : followerList}
          onClose={handleCloseFollowListModal}
          onFollow={handleFollow}
          onUnfollow={handleUnfollow}
          currentUserId={currentUserId}
          followingList={followingList}
        />
      )}
    </div>
  );
}